import { useState, useRef } from "react";
import { Copy, Check, ChevronRight } from "lucide-react";

/* ═══════════════════════════
   CSS  — must be defined FIRST so the IIFE below can reference it
═══════════════════════════ */
const OB_CSS = `
@keyframes ob-glow { 0%,100%{box-shadow:0 0 0 0 rgba(0,212,255,0)} 50%{box-shadow:0 0 16px 2px rgba(0,212,255,.05)} }

.ob-root {
  position:relative; overflow:hidden;
  margin:1.6rem 0; border-radius:4px;
  border:1px solid #1a2f40;
  background:#060c11;
  box-shadow:0 4px 24px rgba(0,0,0,.45), inset 0 1px 0 rgba(0,212,255,.04);
  animation:ob-glow 7s ease infinite;
  will-change:box-shadow;
}

.ob-header {
  display:flex; align-items:center; justify-content:space-between;
  padding:8px 14px;
  background:#0a1520;
  border-bottom:1px solid #1a2f40;
}
.ob-header-left, .ob-header-right { display:flex; align-items:center; gap:8px }

.ob-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0 }
.ob-dot-r { background:#ff5f56; box-shadow:0 0 5px rgba(255,95,86,.35)  }
.ob-dot-y { background:#ffbd2e; box-shadow:0 0 5px rgba(255,189,46,.3)  }
.ob-dot-g { background:#27c93f; box-shadow:0 0 5px rgba(39,201,63,.35)  }

.ob-label {
  display:flex; align-items:center; gap:4px; margin-left:6px;
  font-family:'Share Tech Mono',ui-monospace,monospace;
  font-size:11px; color:#00d4ff; opacity:.5; letter-spacing:.08em;
}
.ob-linecount {
  font-family:'Share Tech Mono',ui-monospace,monospace;
  font-size:10px; color:#2e4a5a; letter-spacing:.04em;
}
.ob-copy {
  display:inline-flex; align-items:center; gap:4px;
  font-family:'Share Tech Mono',ui-monospace,monospace;
  font-size:11px; letter-spacing:.04em;
  color:#2e4a5a; background:transparent; cursor:pointer;
  padding:3px 8px; border-radius:3px; border:1px solid transparent;
  transition:all .18s;
}
.ob-copy:hover { color:#00d4ff; border-color:rgba(0,212,255,.25); background:rgba(0,212,255,.05) }
.ob-copy span  { line-height:1 }

.ob-track {
  position:absolute; top:36px; left:0; width:1px; bottom:0;
  background:linear-gradient(180deg,rgba(0,212,255,.12),transparent);
  pointer-events:none;
}
.ob-body {
  margin:0; padding:.9rem 0 1rem;
  font-family:'Share Tech Mono',ui-monospace,monospace;
  font-size:12.5px; line-height:1.72;
  overflow-x:auto; background:transparent;
}
.ob-line { display:block }
.ob-ln {
  display:inline-block; min-width:3.2rem;
  text-align:right; padding-right:1.2rem;
  user-select:none; color:#1e3348; font-size:11px;
  border-right:1px solid #1a2f40;
  margin-right:.9rem;
}
.ob-text { color:#8faabf }

.ob-hl-open   { color:#00ff88; font-weight:500 }
.ob-hl-closed { color:#2e4a5a }
.ob-hl-pwned  { color:#00ff88; font-weight:700; text-shadow:0 0 8px rgba(0,255,136,.5) }
.ob-hl-error  { color:#ff4455 }
.ob-hl-warn   { color:#ff8c42 }
.ob-hl-hash   { color:#ffbd2e; letter-spacing:.03em }
.ob-hl-ip     { color:#00d4ff }
.ob-hl-path   { color:#e89c4a }
.ob-hl-cve    { color:#cc44ff; font-weight:500 }
.ob-hl-status { color:#00d4ff }
`;

/* ─── style injection — runs once at module load, AFTER OB_CSS is defined ─── */
(function injectStyles() {
  if (typeof document === "undefined") return;
  if (document.querySelector("style[data-ob]")) return;
  const s = document.createElement("style");
  s.setAttribute("data-ob", "1");
  s.textContent = OB_CSS;
  document.head.appendChild(s);
})();

/* ─── highlight patterns ─── */
const PATTERNS = [
  { re: /(\d+\/(?:tcp|udp)\s+open\b.*)/g,                    cls: "ob-hl-open"   },
  { re: /(\d+\/(?:tcp|udp)\s+(?:closed|filtered)\b.*)/g,     cls: "ob-hl-closed" },
  { re: /\b(FOUND|SUCCESS|VALID|PWN(?:ED)?|ROOT|0wnED)\b/gi,  cls: "ob-hl-pwned"  },
  { re: /\b(error|fail(?:ed)?|denied|forbidden|refused|invalid)\b/gi, cls: "ob-hl-error" },
  { re: /\b(warning|warn)\b/gi,                               cls: "ob-hl-warn"   },
  { re: /\b([a-fA-F0-9]{32,64})\b/g,                         cls: "ob-hl-hash"   },
  { re: /\b(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/g,        cls: "ob-hl-ip"     },
  { re: /(\/(?:etc|home|var|tmp|usr|root|opt|proc|sys)\S*)/g, cls: "ob-hl-path"   },
  { re: /((?:[A-Za-z]:\\|\\\\)\S+)/g,                        cls: "ob-hl-path"   },
  { re: /\b(CVE-\d{4}-\d{4,})\b/gi,                          cls: "ob-hl-cve"    },
  { re: /\b(200|201|301|302|400|401|403|404|500|502)\b/g,     cls: "ob-hl-status" },
];

function highlightLine(text) {
  let safe = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  PATTERNS.forEach(({ re, cls }) => {
    safe = safe.replace(re, `<span class="${cls}">$1</span>`);
  });
  return safe;
}

function prepareLines(raw) {
  const lines = raw.split("\n");
  const display = lines[0].trim() === "" ? lines.slice(1) : lines;
  return display.map((line) => highlightLine(line));
}

/* ─── component ─── */
const OutputBlock = ({ children }) => {
  const [copied, setCopied] = useState(false);

  const raw = String(children ?? "").trimEnd();

  // cache highlighted lines — only recompute when children changes
  const cacheRef = useRef(null);
  if (!cacheRef.current || cacheRef.current.raw !== raw) {
    cacheRef.current = { raw, lines: prepareLines(raw) };
  }
  const { lines: htmlLines } = cacheRef.current;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      console.error("Copy failed", e);
    }
  };

  return (
    <div className="ob-root">
      <div className="ob-header">
        <div className="ob-header-left">
          <span className="ob-dot ob-dot-r" />
          <span className="ob-dot ob-dot-y" />
          <span className="ob-dot ob-dot-g" />
          <span className="ob-label">
            <ChevronRight size={11} />
            output
          </span>
        </div>
        <div className="ob-header-right">
          <span className="ob-linecount">{htmlLines.length} lines</span>
          <button className="ob-copy" onClick={handleCopy} aria-label="Copy output">
            {copied
              ? <><Check size={11} /><span>Copied!</span></>
              : <><Copy  size={11} /><span>Copy</span></>
            }
          </button>
        </div>
      </div>

      <div className="ob-track" aria-hidden="true" />

      <pre className="ob-body">
        <code>
          {htmlLines.map((html, i) => (
            <span key={i} className="ob-line">
              <span className="ob-ln">{String(i + 1).padStart(3, " ")}</span>
              <span className="ob-text" dangerouslySetInnerHTML={{ __html: html }} />
              {"\n"}
            </span>
          ))}
        </code>
      </pre>
    </div>
  );
};

export default OutputBlock;
